import { Component, EventEmitter, Input, Output, TemplateRef, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { RecentValuesService, StorageService } from '@luzula/luzula-lib';
import {
	idOf,
	punktartBeschreibung,
	punktartLegendGroups,
	punktartOhneSymbolLabel,
	punktartOhneSymbolColor,
	punktartSvgUrl,
	type PunktartLegendRow,
} from '../../data-model';
import {
	PUNKTART_FAVORITEN_KEY,
	PUNKTART_FAVORITEN_MAX,
	raeumePunktartHistorieAuf,
} from './punktart-favoriten';
// Gemerkt wird beim Speichern des Punktes, nicht hier — siehe choose().

/** Eine selektierbare Zeile im Picker (= Legenden-Zeile + Text-Badge-Vorberechnung). */
interface PickerRow {
	nummer: number;
	beschreibung: string;
	svgUrl: string;
	/** 16/70: kein Symbol -> stattdessen Text-Badge. */
	invisible: boolean;
	/** Text-Badge fuer symbollose Punktarten ('FBF'/'HF'), sonst ''. */
	badge: string;
	/** Badge-Farbe (16 rot, 70 gruen), sonst ''. */
	color: string;
}

interface PickerGroup {
	kategorie: string;
	rows: PickerRow[];
}

/**
 * Visuelle Punktart-Auswahl: ein Knopf zeigt die aktuelle Wahl (Symbol + Nummer +
 * Beschreibung); Klick oeffnet einen Dialog mit Suchfeld, "Haeufig verwendet" und
 * nach Kategorie gruppierten, klickbaren Symbol-Kacheln. Ersetzt das lange
 * "Beschreibung (Nummer)"-Typeahead beim Anlegen — gross genug fuer Touch
 * (Tablet/Telefon), uebersichtlich auf dem Desktop.
 *
 * Die Komponente rendert nur die Auswahl: `type` ist die Punktart-Nummer (= bp.type).
 * Single Source der Symbole/Beschreibungen/Kategorien ist die zentrale
 * Punktarten-Liste (punktartLegendGroups), identisch zur Karten-Legende.
 *
 * **Die Favoriten holt sich das Fenster selbst** (`punktart-favoriten.ts`), bei
 * jedem Oeffnen. Frueher kamen sie von aussen, und nur das Anlege-Fenster gab sie
 * mit — beim spaeteren Aendern der Punktart (Reiter Aufnahme/Kurzprotokoll)
 * fehlte die Abkuerzung deshalb ganz. Ein Fenster, ein Verhalten: Man muss sich
 * nicht merken, wo sie geht und wo nicht.
 *
 * **Geschrieben wird hier nichts.** Eine Wahl wird erst gemerkt, wenn der Punkt
 * gespeichert ist; das tun die Fenster, die den Punkt halten.
 */
@Component({
	standalone: true,
	selector: 'bp-punktart-picker',
	imports: [CommonModule, FormsModule],
	templateUrl: './punktart-picker.component.html',
	styleUrls: ['./punktart-picker.component.css'],
})
export class PunktartPickerComponent {
	/** Aktuelle Punktart (= Nummer als String, z.B. "6"). */
	@Input() type = '';
	@Output() typeChange = new EventEmitter<string>();

	@ViewChild('pickerModal', { static: true }) pickerModal!: TemplateRef<unknown>;
	private modalService = inject(NgbModal);
	private modalRef?: NgbModalRef;
	private recentValues = inject(RecentValuesService);
	private storage = inject(StorageService);

	/** Nach Kategorie gruppierte Kacheln (Browse-Ansicht); beim Oeffnen befuellt. */
	groups: PickerGroup[] = [];
	/** Flache Liste aller Kacheln (Suche + Favoriten-Lookup). */
	allRows: PickerRow[] = [];
	/** Die haeufigsten Punktarten (Nummern), haeufigste zuerst. */
	favoriten: string[] = [];
	/** Aktueller Suchbegriff (leer = Browse-Ansicht). */
	query = '';
	/**
	 * Auf Desktop (Maus/feiner Zeiger) soll der Cursor beim Oeffnen direkt im
	 * Suchfeld stehen, damit man wie zuvor sofort tippen kann. Auf Touch
	 * (Tablet/Telefon) NICHT — sonst verdeckt die eingeblendete Tastatur die
	 * Symbol-Liste, die dort der Hauptweg ist.
	 */
	autofocusSearch = false;

	// --- Trigger-Knopf: aktuelle Auswahl ---
	get currentNummer(): number {
		return idOf(this.type);
	}
	get hasSelection(): boolean {
		return Number.isFinite(this.currentNummer) && this.currentNummer > 0;
	}
	get currentSvgUrl(): string {
		return punktartSvgUrl(this.type);
	}
	get currentBeschreibung(): string {
		return punktartBeschreibung(this.type);
	}
	/** Nicht-leer => symbollose Punktart (Text-Badge statt Icon). */
	get currentBadge(): string {
		return punktartOhneSymbolLabel(this.type);
	}
	/** Farbe des aktuellen Text-Badges (16 rot, 70 gruen), sonst ''. */
	get currentColor(): string {
		return punktartOhneSymbolColor(this.type);
	}

	get searching(): boolean {
		return this.query.trim().length > 0;
	}

	/** Treffer fuer den aktuellen Suchbegriff: Nummer-Prefix zuerst, dann nach Nummer. */
	get filtered(): PickerRow[] {
		const q = this.query.trim().toLowerCase();
		if (!q) return [];
		return this.allRows
			.filter(r => r.beschreibung.toLowerCase().includes(q) || String(r.nummer).includes(q))
			.sort((a, b) => {
				const ap = String(a.nummer).startsWith(q) ? 0 : 1;
				const bp = String(b.nummer).startsWith(q) ? 0 : 1;
				if (ap !== bp) return ap - bp;
				return a.nummer - b.nummer;
			});
	}

	/** Favoriten als Kacheln (Reihenfolge erhalten, Unbekanntes raus). */
	get favoritenRows(): PickerRow[] {
		const byNummer = new Map(this.allRows.map(r => [r.nummer, r]));
		return this.favoriten
			.map(t => byNummer.get(idOf(t)))
			.filter((r): r is PickerRow => !!r);
	}

	/** Gruppen + flache Liste aus der zentralen Punktarten-Liste aufbauen. */
	loadRows(): void {
		this.groups = punktartLegendGroups().map(g => ({
			kategorie: g.kategorie,
			rows: g.rows.map(r => this.toPickerRow(r)),
		}));
		this.allRows = this.groups.flatMap(g => g.rows);
	}

	private toPickerRow(r: PunktartLegendRow): PickerRow {
		return {
			nummer: r.nummer,
			beschreibung: r.beschreibung,
			svgUrl: r.svgUrl,
			invisible: r.invisible,
			badge: punktartOhneSymbolLabel(String(r.nummer)),
			color: punktartOhneSymbolColor(String(r.nummer)),
		};
	}

	open(): void {
		this.loadRows();
		this.ladeFavoriten();
		this.query = '';
		this.autofocusSearch = this.prefersPointerKeyboard();
		this.modalRef = this.modalService.open(this.pickerModal, { scrollable: true, size: 'lg' });
		// Abweisen (Backdrop/ESC/X) ist kein Fehler — Auswahl bleibt unveraendert.
		this.modalRef.result.catch(() => undefined);
	}

	/** True auf Geraeten mit Maus/feinem Zeiger (Desktop) — dort Suchfeld fokussieren. */
	private prefersPointerKeyboard(): boolean {
		return typeof window !== 'undefined'
			&& typeof window.matchMedia === 'function'
			&& window.matchMedia('(hover: hover) and (pointer: fine)').matches;
	}

	/**
	 * Favoriten frisch lesen — jedes Oeffnen, denn gewaehlt wird auch in anderen
	 * Fenstern (Anlegen, Aufnahme, Kurzprotokoll), und alle teilen sich eine Liste.
	 */
	ladeFavoriten(): void {
		raeumePunktartHistorieAuf(this.storage);
		this.favoriten = this.recentValues.top(PUNKTART_FAVORITEN_KEY, PUNKTART_FAVORITEN_MAX);
	}

	/**
	 * Kachel gewaehlt: Nummer uebernehmen, nach oben melden, Dialog schliessen.
	 *
	 * **Gemerkt wird hier NICHTS.** Das geschieht erst beim Speichern des Punktes
	 * (`merkeGewaehltePunktart`) — wer antippt und dann abbricht, hat sich fuer
	 * nichts entschieden.
	 */
	choose(nummer: number): void {
		const t = String(nummer);
		this.type = t;
		this.typeChange.emit(t);
		this.modalRef?.close(t);
	}
}
